import * as React from 'react';

import { ApolloError } from 'apollo-client';
import { Mutation, Query } from 'react-apollo';
import { InjectedRouter } from 'react-router';

import ToastActions from 'actions/ToastActions';
// @ts-ignore
import { touch } from 'actions/touchActions';
import { withRouter } from 'app/context/Router';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import CurrentUser from 'auth/CurrentUser';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/ButtonDeprecated';
import Dialog from 'components/Dialog';
import GroupAvatar from 'groups/components/GroupAvatar';
import JoinGroupAvatarPanel from 'join/components/JoinGroupAvatarPanel';
import JoinGroupModalRequestToJoin from 'groups/components/JoinGroupModalRequestToJoin';
import Link from 'components/Link';
import LoadingModal from 'components/LoadingModal';
import { TOUCH_KEY as NAV_TOUCH_KEY } from 'components/NavigationList/NavigationList';
import Row from 'components/Row';
import { loadGQLUserGroupByCode } from 'groups/lib/GroupPromises';

import JoinFlowGroupStorage from '../data/JoinFlowGroupStorage';
import JOIN_GROUP_INFO_QUERY from 'join/queries/JoinGroupInfoQuery.gql';
import JOIN_GROUP_BY_CODE_MUTATION from 'groups/components/mutations/JoinGroupByCode.gql';

import {
  joinGroupByCodeMutation,
  joinGroupByCodeMutationVariables,
} from 'groups/components/mutations/__generated__/joinGroupByCodeMutation';
import {
  JoinGroupInfo,
  JoinGroupInfo_groupsByCode as Group,
  JoinGroupInfo_groupsByCode_owners as GroupOwner,
  JoinGroupInfoVariables,
} from 'join/queries/__generated__/JoinGroupInfo';

type Props = {
  code: string;
  onRequestHide: () => {};
  onGroupNotFound?: () => {};
  router: InjectedRouter;
};

type State = {
  checkingAlreadyConnected: boolean;
  requestToJoinModalActive: boolean;
};

class JoinConfirmSubscriptionModal extends React.Component<Props, State> {
  static defaultProps = {
    onRequestHide: () => undefined,
  };

  state: State = {
    checkingAlreadyConnected: true,
    requestToJoinModalActive: false,
  };

  async componentDidMount() {
    const userGroup = await this.checkIfAlreadyConnected();

    if (userGroup) {
      this.handleUserIsAlreadyConnected();
    }

    this.setState({ checkingAlreadyConnected: false });
  }

  handleGroupNotFound = () => {
    const { onRequestHide, onGroupNotFound, router } = this.props;

    ToastActions.showErrorToast('Group not found.');
    onRequestHide();

    if (onGroupNotFound) {
      onGroupNotFound();
    }

    router.push('/classes');
  };

  handleGroupCannotJoin = () => {
    const { onRequestHide, router } = this.props;
    ToastActions.showErrorToast('Can’t join this class');
    onRequestHide();
    router.push('/classes');
  };

  checkIfAlreadyConnected = async () => {
    const { code } = this.props;
    const group = await loadGQLUserGroupByCode(code);
    return group;
  };

  handleUserIsAlreadyConnected = () => {
    const { code, onRequestHide, router } = this.props;
    ToastActions.showSuccessToast('You have already joined this class.');
    onRequestHide();
    router.push(`/classes/${code}`);
  };

  signOutRequest = () => {
    AuthActions.signOut(`/join/${this.props.code}`, true);
  };

  handleJoinCompleted = (group: Group) => {
    const { code, className } = group;
    JoinFlowGroupStorage.setJoinedClassName(className);
    ToastActions.showSuccessToast(`You’ve joined ${className}`);
    touch(NAV_TOUCH_KEY);
    this.props.router.push(`/classes/${code}`);
  };

  handleJoinErrored = (error: ApolloError) => {
    ToastActions.showErrorToast(error.message);
    this.props.onRequestHide();
  };

  generatePossessiveSignature = (owners: GroupOwner[]) => {
    if (owners.length > 0) {
      return `${owners.map(o => o.displayName).join(', ')}'s`;
    }

    // We shouldn't get here but just in case there are no owners
    return '';
  };

  renderRequestToJoinDialog = (group: Group) => {
    const { onRequestHide, router } = this.props;
    const { className, code, organization, groupAvatar } = group;

    return (
      <Dialog
        {...this.props}
        size="small"
        className="JoinRequestToJoinModal"
        title="Request to join class"
        eventScreenName="JoinRequestToJoinModal">
        <BoxBody>
          <JoinGroupModalRequestToJoin
            className={className}
            groupCode={code}
            schoolName={organization && organization.name}
            onSubmit={() => router.push(`/classes/${code}`)}
            onCancel={onRequestHide}
            groupAvatar={<GroupAvatar group={{ groupAvatar }} />}
          />
        </BoxBody>
      </Dialog>
    );
  };

  closeDialog = () => {
    const { onRequestHide, router } = this.props;
    onRequestHide();
    router.push('/classes');
  };

  renderJoinDialog = (group: Group, onClick: () => void) => {
    const { className, code, organization, groupAvatar, owners } = group;
    const groupAvatarComponent = <GroupAvatar group={{ groupAvatar }} />;

    return (
      <Dialog
        {...this.props}
        onDialogDismiss={this.closeDialog}
        size="xxsmall"
        className="JoinConfirmSubscriptionModal"
        eventScreenName="JoinConfirmSubscriptionModal">
        <BoxBody flush>
          <Row>
            <JoinGroupAvatarPanel
              groupAvatar={groupAvatarComponent}
              possessiveSignature={this.generatePossessiveSignature(owners)}
              orgName={organization && organization.name}
              groupCode={code}
              className={className}
            />
          </Row>
          <Button full onClick={onClick} className="JoinConfirmSubscriptionModal-button">
            Join class
          </Button>
        </BoxBody>
        <BoxFooter hideBorder>
          {`Logged in as ${CurrentUser.getUser().getSignature()}`}
          <br />
          Not you? <Link onClick={this.signOutRequest}>Log out</Link>
        </BoxFooter>
      </Dialog>
    );
  };

  showRequestToJoinDialog = () => this.setState({ requestToJoinModalActive: true });

  render() {
    const { code } = this.props;
    const { checkingAlreadyConnected, requestToJoinModalActive } = this.state;

    if (checkingAlreadyConnected) {
      return <LoadingModal />;
    }

    return (
      <Query<JoinGroupInfo, JoinGroupInfoVariables>
        query={JOIN_GROUP_INFO_QUERY}
        variables={{ code }}>
        {({ data, loading }) => {
          if (loading) {
            return <LoadingModal />;
          }

          const group = data && data.groupsByCode[0];

          if (!group) {
            this.handleGroupNotFound();
            return null;
          }

          if (!group.canJoin) {
            this.handleGroupCannotJoin();
            return null;
          }

          return (
            <Mutation<joinGroupByCodeMutation, joinGroupByCodeMutationVariables>
              mutation={JOIN_GROUP_BY_CODE_MUTATION}
              onCompleted={() => this.handleJoinCompleted(group)}
              onError={error => this.handleJoinErrored(error)}>
              {joinGroupByCodeMutation => {
                if (requestToJoinModalActive) {
                  return this.renderRequestToJoinDialog(group);
                } else if (group.requestToJoinEnabled && group.requestToJoinRequired) {
                  return this.renderJoinDialog(group, this.showRequestToJoinDialog);
                } else {
                  const onClick = () =>
                    joinGroupByCodeMutation({ variables: { input: { groupCode: code } } });
                  return this.renderJoinDialog(group, onClick);
                }
              }}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export { JoinConfirmSubscriptionModal };

export default withRouter(JoinConfirmSubscriptionModal);
